import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  QrCode, Copy, Store, Plus, Loader2, TrendingUp, MousePointerClick,
  Receipt, Package, LogOut, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BrandLogo } from "@/components/BrandLogo";

const fmt = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800", approved: "bg-cyan-100 text-cyan-800",
  paid: "bg-emerald-100 text-emerald-800", invoiced: "bg-blue-100 text-blue-800",
  rejected: "bg-rose-100 text-rose-700", cancelled: "bg-slate-100 text-slate-600",
  flagged: "bg-rose-100 text-rose-700",
};

export default function PartnerHub() {
  const { user, logout } = useAuth();
  const [params] = useSearchParams();
  const pidOverride = params.get("partner_id");
  const qs = pidOverride ? `?partner_id=${encodeURIComponent(pidOverride)}` : "";
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/partner-hub/me${qs}`)
      .then(({ data }) => setMe(data))
      .catch((e) => setError(e.response?.data?.detail || "Could not load your partner account"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="partner-hub" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <BrandLogo to="/" size="sm" />
          <div className="flex items-center gap-3 text-sm text-slate-600">
            {me?.partner?.business_name && <span className="font-semibold">{me.partner.business_name}</span>}
            {user && (
              <button onClick={logout} data-testid="hub-signout" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-600">AWARE Partner Hub</div>
        <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">Your Partner tools</h1>

        {error && (
          <div data-testid="hub-error" className="mt-6 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
            {error}{user?.role === "admin" && !pidOverride ? " — as admin, append ?partner_id=… to preview a partner's hub." : ""}
          </div>
        )}

        {me?.capabilities?.can_refer && <ReferralEarnings qs={qs} />}
        {me?.capabilities?.can_retail && <RetailCounter me={me} qs={qs} />}
        {me && !me.capabilities?.can_refer && !me.capabilities?.can_retail && (
          <div className="mt-6 rounded-2xl bg-white border border-slate-200 p-5 text-sm text-slate-600" data-testid="hub-no-capabilities">
            Your Partner account doesn't have referral or retail capabilities enabled yet.
            Contact AWARE to discuss your Partner arrangement.
          </div>
        )}
      </main>
    </div>
  );
}

function ReferralEarnings({ qs }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/partner-hub/referral-earnings${qs}`).then(({ data }) => setData(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!data) return null;

  const copy = async () => {
    try { await navigator.clipboard.writeText(data.url); toast.success("Link copied"); }
    catch { toast.error("Copy failed — select the link manually"); }
  };

  return (
    <section className="mt-8" data-testid="hub-referral-section">
      <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
        <TrendingUp className="w-4.5 h-4.5 w-5 h-5 text-cyan-600" /> Referral earnings
      </h2>
      <div className="mt-3 grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          ["Link visits", data.visits, MousePointerClick],
          ["Attributed bookings", data.referral_count, Receipt],
          ["Pending", `$${data.totals.pending.toFixed(2)}`, null],
          ["Approved", `$${data.totals.approved.toFixed(2)}`, null],
          ["Paid", `$${data.totals.paid.toFixed(2)}`, null],
        ].map(([label, value]) => (
          <div key={label} className="rounded-2xl bg-white border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{label}</div>
            <div className="mt-1 font-display text-2xl font-bold text-slate-900" data-testid={`hub-stat-${String(label).toLowerCase().replace(/ /g, "-")}`}>{value}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-2xl bg-white border border-slate-200 p-4 flex items-center gap-2">
        <QrCode className="w-4 h-4 text-cyan-600 shrink-0" />
        <Input readOnly value={data.url} data-testid="hub-referral-url" className="h-9 rounded-xl text-xs" />
        <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={copy} data-testid="hub-referral-copy">
          <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
        </Button>
        <Button
          asChild
          size="sm"
          variant="outline"
          className="rounded-full shrink-0"
          data-testid="hub-referral-open"
        >
          <a href={data.url} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open
          </a>
        </Button>
      </div>
      <p className="mt-1.5 text-[11px] text-slate-500">
        This is the same booking link sent in your trade portal invite email —
        every water test booked through it is automatically attributed to you.
      </p>
      {data.commissions.length > 0 && (
        <div className="mt-3 bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-right px-4 py-2.5">Commission</th>
                <th className="text-left px-4 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody data-testid="hub-commissions-table">
              {data.commissions.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 text-xs text-slate-600">{(c.earned_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-700">{(c.revenue_kind || "").replace(/_/g, " ")}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-slate-900">${Number(c.commission_amount || 0).toFixed(2)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_COLORS[c.status] || "bg-slate-100 text-slate-600"}`}>{c.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function RetailCounter({ me, qs }) {
  const [rows, setRows] = useState(null);
  const [reorders, setReorders] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showReorder, setShowReorder] = useState(false);

  const load = () => {
    api.get(`/partner-hub/retail-bookings${qs}`).then(({ data }) => setRows(data)).catch(() => {});
    api.get(`/partner-hub/kit-reorders${qs}`).then(({ data }) => setReorders(data.rows || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const tests = (me.retail?.tests || []).filter((t) => t.wholesale_cents != null);
  const kitWholesale = me.retail?.terms?.kit_wholesale_cents;

  return (
    <section className="mt-10" data-testid="hub-retail-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2">
          <Store className="w-5 h-5 text-cyan-600" /> Retail counter
        </h2>
        <div className="flex items-center gap-2">
          {kitWholesale != null && (
            <Button size="sm" variant="outline" onClick={() => setShowReorder(true)}
              data-testid="hub-reorder-kits" className="rounded-full font-semibold">
              <Package className="w-4 h-4 mr-1.5" /> Reorder kits
            </Button>
          )}
          <Button size="sm" onClick={() => setShowNew(true)} data-testid="hub-new-counter-booking"
            className="btn-aqua rounded-full font-semibold" disabled={tests.length === 0}>
            <Plus className="w-4 h-4 mr-1.5" /> New counter booking
          </Button>
        </div>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Take the customer's payment in-store at your own retail price, hand over the kit, and
        create the AWARE booking here. AWARE invoices you the wholesale amount on your account terms.
      </p>
      {tests.length === 0 && (
        <div className="mt-3 rounded-2xl bg-amber-50 border border-amber-200 p-4 text-xs text-amber-800" data-testid="hub-no-terms">
          No wholesale terms configured yet — contact AWARE to activate your retail pricing.
        </div>
      )}
      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-2xl bg-slate-900 text-white p-4">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Uninvoiced balance</div>
          <div className="mt-1 font-display text-2xl font-bold" data-testid="hub-retail-balance">{fmt(rows?.pending_balance_cents)}</div>
        </div>
        {tests.map((t) => (
          <div key={t.id} className="rounded-2xl bg-white border border-slate-200 p-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{t.name}</div>
            <div className="mt-1 font-display text-xl font-bold text-slate-900">{fmt(t.wholesale_cents)}</div>
            <div className="text-[10px] text-slate-400">your wholesale</div>
          </div>
        ))}
      </div>

      {rows?.rows?.length > 0 && (
        <div className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">Reference</th>
                <th className="text-left px-4 py-2.5">Customer</th>
                <th className="text-left px-4 py-2.5">Test</th>
                <th className="text-right px-4 py-2.5">Net charge</th>
                <th className="text-left px-4 py-2.5">Invoice</th>
              </tr>
            </thead>
            <tbody data-testid="hub-retail-table">
              {rows.rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-4 py-2.5 text-xs text-slate-600">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-2.5 text-xs font-mono text-slate-700">{r.booking_reference}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-700">{r.customer_name || "—"}</td>
                  <td className="px-4 py-2.5 text-xs text-slate-700">{r.test_name}</td>
                  <td className="px-4 py-2.5 text-right text-xs font-semibold text-slate-900">
                    {fmt(r.net_cents)}
                    {r.kit_from_stock ? <span className="ml-1 text-[9px] text-emerald-600 font-bold">KIT CR</span> : null}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {reorders.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Kit reorders</div>
          <div className="mt-2 bg-white rounded-2xl border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <tbody data-testid="hub-reorders-table">
                {reorders.map((r) => (
                  <tr key={r.id} className="border-t first:border-t-0 border-slate-100">
                    <td className="px-4 py-2.5 text-xs text-slate-600">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-700">{r.quantity} kit{r.quantity !== 1 ? "s" : ""} · {fmt(r.unit_cents)} each</td>
                    <td className="px-4 py-2.5 text-right text-xs font-semibold text-slate-900">{fmt(r.total_cents)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.status === "shipped" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <NewCounterBooking open={showNew} onClose={() => setShowNew(false)} tests={tests}
        kitCredit={me.retail?.terms?.kit_credit_cents || 0} qs={qs} onCreated={load} />
      <ReorderKitsDialog open={showReorder} onClose={() => setShowReorder(false)}
        unitCents={kitWholesale} qs={qs} onCreated={load} />
    </section>
  );
}

function ReorderKitsDialog({ open, onClose, unitCents, qs, onCreated }) {
  const [qty, setQty] = useState("10");
  const [busy, setBusy] = useState(false);
  const pidOverride = new URLSearchParams(qs).get("partner_id");
  const n = parseInt(qty, 10);

  const submit = async () => {
    if (!n || n < 1 || n > 200) return toast.error("Quantity must be 1–200");
    setBusy(true);
    try {
      await api.post("/partner-hub/kit-reorders", {
        quantity: n, partner_id: pidOverride || undefined,
      });
      toast.success(`Reorder placed — ${n} kits requested`);
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not place reorder");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="reorder-dialog">
        <DialogHeader><DialogTitle>Reorder AWARE kits</DialogTitle></DialogHeader>
        <div>
          <Label className="text-xs font-semibold text-slate-600">Quantity</Label>
          <Input data-testid="reorder-qty" value={qty} inputMode="numeric"
            onChange={(e) => setQty(e.target.value.replace(/[^0-9]/g, ""))}
            className="mt-1 h-10 rounded-xl" />
        </div>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="reorder-summary">
          {fmt(unitCents)} per kit{n >= 1 ? <> · total <b>{fmt(unitCents * n)}</b></> : null} — invoiced
          on your account terms once AWARE ships your kits.
        </div>
        <Button onClick={submit} disabled={busy} data-testid="reorder-submit" className="btn-aqua rounded-full font-semibold w-full">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Place reorder"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function NewCounterBooking({ open, onClose, tests, kitCredit, qs, onCreated }) {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", address: "" });
  const [testId, setTestId] = useState("");
  const [kitFromStock, setKitFromStock] = useState(true);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pidOverride = new URLSearchParams(qs).get("partner_id");

  const submit = async () => {
    if (!testId) return toast.error("Choose a test");
    if (!form.name || !form.email || !form.mobile || !form.address)
      return toast.error("All customer fields are required");
    setBusy(true);
    try {
      const { data } = await api.post("/partner-hub/retail-bookings", {
        test_id: testId, customer: form, kit_from_stock: kitFromStock,
        partner_id: pidOverride || undefined,
      });
      toast.success(`Booking ${data.booking_reference} created — customer confirmation emailed`);
      setForm({ name: "", email: "", mobile: "", address: "" });
      setTestId("");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create booking");
    } finally { setBusy(false); }
  };

  const selected = tests.find((t) => t.id === testId);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="counter-booking-dialog">
        <DialogHeader><DialogTitle>New counter booking</DialogTitle></DialogHeader>
        <div>
          <Label className="text-xs font-semibold text-slate-600">Test</Label>
          <Select value={testId} onValueChange={setTestId}>
            <SelectTrigger data-testid="counter-test-select" className="mt-1 h-10 rounded-xl">
              <SelectValue placeholder="Choose a test" />
            </SelectTrigger>
            <SelectContent>
              {tests.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name} · wholesale {fmt(t.wholesale_cents)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[["name", "Customer name"], ["email", "Customer email"], ["mobile", "Mobile"], ["address", "Property address"]].map(([k, label]) => (
            <div key={k} className={k === "address" || k === "email" ? "col-span-2" : ""}>
              <Label className="text-xs font-semibold text-slate-600">{label}</Label>
              <Input data-testid={`counter-${k}`} value={form[k]} onChange={set(k)} className="mt-1 h-10 rounded-xl" />
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setKitFromStock(!kitFromStock)} data-testid="counter-kit-toggle"
          className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm text-left transition-colors ${kitFromStock ? "border-emerald-400 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-white text-slate-600"}`}>
          <Package className="w-4 h-4 shrink-0" />
          <span>
            AWARE kit supplied from my purchased stock
            {kitFromStock && kitCredit > 0 && <span className="font-semibold"> — {fmt(kitCredit)} kit credit applies</span>}
          </span>
        </button>
        {selected && (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="counter-summary">
            You'll be invoiced <b>{fmt(Math.max(selected.wholesale_cents - (kitFromStock ? kitCredit : 0), 0))}</b> for
            this booking on your account terms. The customer pays your retail price in-store and never sees wholesale pricing.
          </div>
        )}
        <Button onClick={submit} disabled={busy} data-testid="counter-submit" className="btn-aqua rounded-full font-semibold w-full">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create booking & email customer"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
